import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  Chip,
  Avatar
} from '@mui/material';
import {
  Call as CallIcon,
  CallEnd as CallEndIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { getVoiceToken, initiateVoiceCall, endCall } from '../../services/twilioService';
import { Device } from 'twilio-client';

/**
 * TwilioVoice Component
 * 
 * This component provides a voice call interface using Twilio Voice.
 * It handles connecting to a call, managing audio, and UI controls.
 * 
 * @param {Object} props
 * @param {Object} props.consultation - The consultation object
 * @param {Function} props.onClose - Callback when the voice call is closed
 */
const TwilioVoice = ({ consultation, onClose }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [device, setDevice] = useState(null);
  const [call, setCall] = useState(null);
  const [callStatus, setCallStatus] = useState('disconnected'); // disconnected, connecting, connected, completed
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [callDuration, setCallDuration] = useState(0);
  const [callSid, setCallSid] = useState(null);
  
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  // Initialize the voice call
  useEffect(() => {
    const initializeVoiceCall = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get a token for the doctor
        const identity = `doctor_${Date.now()}`;
        const token = await getVoiceToken(identity);

        // Initialize the Twilio Device
        const deviceInstance = new Device();
        
        // Set up event listeners
        deviceInstance.on('ready', () => {
          console.log('Twilio Device is ready for calls');
          setLoading(false);
        });
        
        deviceInstance.on('error', (error) => {
          console.error('Twilio Device error:', error);
          setError(`Call error: ${error.message}`);
        });
        
        deviceInstance.on('connect', (conn) => {
          setCall(conn);
          setCallStatus('connected');
          setCallSid(conn.parameters.CallSid);
          
          // Start the call timer
          startCallTimer();
          
          conn.on('disconnect', () => {
            setCallStatus('completed');
            stopCallTimer();
          });
        });
        
        deviceInstance.on('disconnect', () => {
          setCallStatus('disconnected');
          setCall(null);
          stopCallTimer();
        });
        
        // Initialize the device with the token
        await deviceInstance.setup(token);
        
        setDevice(deviceInstance);
        setLoading(false);
      } catch (err) {
        console.error('Error initializing voice call:', err);
        setError('Failed to initialize voice call. Please try again.');
        setLoading(false);
      }
    };

    if (consultation) {
      initializeVoiceCall();
    }

    // Clean up function
    return () => {
      // Disconnect any active call
      if (call) {
        call.disconnect();
      }
      
      // Release the Twilio Device
      if (device) {
        device.destroy();
      }
      
      // Clear the timer
      stopCallTimer();
    };
  }, [consultation]);

  // Start the call timer
  const startCallTimer = () => {
    setCallDuration(0);
    timerRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
  };

  // Stop the call timer
  const stopCallTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Format the call duration as MM:SS
  const formatCallDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Make a call
  const makeCall = async () => {
    try {
      setCallStatus('connecting');
      
      // In a real implementation, you would get the patient's phone number from the consultation
      const patientPhone = '+1234567890'; // Mock phone number
      
      // Initiate the call through the server
      const callDetails = await initiateVoiceCall(patientPhone, device.identity);
      
      // The call will be connected through the device's connect event
      console.log('Call initiated:', callDetails);
    } catch (err) {
      console.error('Error making call:', err);
      setError('Failed to make call. Please try again.');
      setCallStatus('disconnected');
    }
  };

  // End the call
  const hangUp = async () => {
    if (call) {
      call.disconnect();
    }
    
    if (callSid) {
      try {
        // End the call on the server
        await endCall(callSid);
      } catch (err) {
        console.error('Error ending call on server:', err);
      }
    }
    
    setCallStatus('disconnected');
    setCall(null);
    stopCallTimer();
  };

  // Toggle mute
  const toggleMute = () => {
    if (call) {
      if (isMuted) {
        call.mute(false);
        setIsMuted(false);
      } else {
        call.mute(true);
        setIsMuted(true);
      }
    }
  };

  // Toggle speaker
  const toggleSpeaker = () => {
    if (audioRef.current) {
      audioRef.current.volume = isSpeakerOn ? 0 : 1;
      setIsSpeakerOn(!isSpeakerOn);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 3 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Initializing voice call...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" color="primary" onClick={onClose}>
          Back to Consultation
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, height: '100%' }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          height: '100%',
          borderRadius: 2
        }}
      >
        {/* Patient info */}
        <Avatar 
          sx={{ 
            width: 80, 
            height: 80, 
            mb: 2,
            bgcolor: theme.palette.primary.main
          }}
        >
          <PersonIcon sx={{ fontSize: 40 }} />
        </Avatar>
        
        <Typography variant="h5" sx={{ mb: 1 }}>
          {consultation.patientName}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {consultation.patientAge} years, {consultation.patientGender}
        </Typography>
        
        {/* Call status */}
        <Chip 
          label={
            callStatus === 'disconnected' ? 'Ready to Call' : 
            callStatus === 'connecting' ? 'Connecting...' : 
            callStatus === 'connected' ? 'On Call' : 
            'Call Ended'
          } 
          color={
            callStatus === 'disconnected' ? 'default' : 
            callStatus === 'connecting' ? 'warning' : 
            callStatus === 'connected' ? 'success' : 
            'error'
          }
          sx={{ mb: 3, px: 2, py: 3, fontSize: '1rem' }}
        />
        
        {/* Call duration */}
        {callStatus === 'connected' && (
          <Typography variant="h4" sx={{ mb: 3, fontFamily: 'monospace' }}>
            {formatCallDuration(callDuration)}
          </Typography>
        )}
        
        {/* Call controls */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          {callStatus === 'disconnected' && (
            <IconButton 
              onClick={makeCall} 
              color="success"
              sx={{ 
                mx: 1, 
                p: 2,
                bgcolor: alpha(theme.palette.success.main, 0.1),
                '&:hover': {
                  bgcolor: alpha(theme.palette.success.main, 0.2)
                }
              }}
            >
              <CallIcon sx={{ fontSize: 36 }} />
            </IconButton>
          )}
          
          {(callStatus === 'connecting' || callStatus === 'connected') && (
            <>
              <IconButton 
                onClick={toggleMute} 
                color={isMuted ? 'error' : 'primary'}
                sx={{ 
                  mx: 1, 
                  p: 2,
                  bgcolor: alpha(isMuted ? theme.palette.error.main : theme.palette.primary.main, 0.1),
                  '&:hover': {
                    bgcolor: alpha(isMuted ? theme.palette.error.main : theme.palette.primary.main, 0.2)
                  }
                }}
              >
                {isMuted ? <MicOffIcon sx={{ fontSize: 28 }} /> : <MicIcon sx={{ fontSize: 28 }} />}
              </IconButton>
              
              <IconButton 
                onClick={hangUp} 
                color="error"
                sx={{ 
                  mx: 1, 
                  p: 2,
                  bgcolor: theme.palette.error.main,
                  color: 'white',
                  '&:hover': {
                    bgcolor: theme.palette.error.dark
                  }
                }}
              >
                <CallEndIcon sx={{ fontSize: 36 }} />
              </IconButton>
              
              <IconButton 
                onClick={toggleSpeaker} 
                color={isSpeakerOn ? 'primary' : 'default'}
                sx={{ 
                  mx: 1, 
                  p: 2,
                  bgcolor: alpha(isSpeakerOn ? theme.palette.primary.main : theme.palette.grey[500], 0.1),
                  '&:hover': {
                    bgcolor: alpha(isSpeakerOn ? theme.palette.primary.main : theme.palette.grey[500], 0.2)
                  }
                }}
              >
                {isSpeakerOn ? <VolumeUpIcon sx={{ fontSize: 28 }} /> : <VolumeOffIcon sx={{ fontSize: 28 }} />}
              </IconButton>
            </>
          )}
          
          {callStatus === 'completed' && (
            <Button 
              variant="contained" 
              color="primary" 
              onClick={onClose}
              sx={{ mt: 2 }}
            >
              Back to Consultation
            </Button>
          )}
        </Box>
        
        {/* Hidden audio element for the call */}
        <audio ref={audioRef} />
      </Paper>
    </Box>
  );
};

export default TwilioVoice; 